import { Box, Center, FormHelperText, FormLabel, Grid, IconButton, Stack } from '@chakra-ui/react';
import { produce } from 'immer';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Conceal } from '../../../components/display/conceal';
import { FormErrorMessage } from '../../../components/form/form-error-message';
import { UserEmailSelector } from '../../../components/form/search/user-email-selector';
import { Button } from '../../../design-system/button';
import { rem } from '../../../design-system/design-system';
import { PlusSmOutline, TrashOutline } from '../../../design-system/icon';
import { loadSearchUsers } from '../../../queries/users';
import { User } from '../../../schemas/users';

type ApprovalProcessProps = {
	error?: string;
	onChange: (value: User[][]) => void;
	value: User[][];
};

export const ApprovalProcess: FC<ApprovalProcessProps> = ({ error, onChange, value }) => {
	const { t } = useTranslation('incentives', { keyPrefix: 'form' });

	const handleChangeLevel = (index: number, users: User[]) => {
		const updated = produce(value, (draft) => {
			draft[index] = users;
		});

		onChange(updated);
	};

	const handleSearch = async (query: string) => {
		const allUsers = value.flat();
		const users = await loadSearchUsers({ search: query });
		return users.filter((user) => allUsers.every((u) => u.id !== user.id));
	};

	const handleAddLevel = () => {
		onChange([...value, []]);
	};

	const handleRemoveLevel = (index: number) => {
		const updated = produce(value, (draft) => {
			draft.splice(index, 1);
		});

		onChange(updated);
	};

	return (
		<>
			<FormLabel>{t('approvalProcessLabel')}</FormLabel>
			<FormHelperText mt={0} mb={2}>
				{t('approvalProcessHelper')}
			</FormHelperText>

			<Stack spacing={2} mb={2}>
				{value.map((level, index) => {
					return (
						<Box key={index}>
							<FormLabel mb={1}>{t(`approvalStep${index + 1}Label`)}</FormLabel>

							<Grid columnGap={2} templateColumns={`1fr ${rem(40)}`}>
								<UserEmailSelector flexGrow={1} onChange={(value) => handleChangeLevel(index, value)} onSearch={handleSearch} value={level} />

								{index > 0 ? (
									<Center>
										<IconButton aria-label={t('removeApprovalStep')} isRound onClick={() => handleRemoveLevel(index)}>
											<TrashOutline />
										</IconButton>
									</Center>
								) : (
									<Box />
								)}
							</Grid>
						</Box>
					);
				})}
			</Stack>

			<FormErrorMessage>{error}</FormErrorMessage>

			<Conceal if={value.length === 4}>
				<Button leftIcon={<PlusSmOutline />} onClick={handleAddLevel} variant="ghost">
					{t('addApprovalStep')}
				</Button>
			</Conceal>
		</>
	);
};
